import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { colors, breakpoints, fadeIn } from '../../../assets/styles/GlobalStyles';
import MainLayout from '../../../layouts/MainLayout';
import { useAuth } from '../../../contexts/AuthContext';
import { authService } from '../../../services';
import { getContextoAcesso, podeAcessarCertidoesOnline } from '../../requerimentos/core/services/acessoPedidoService';
import { certidaoNadaConstaService, formatarDataCertidao, CODIGOS_ERRO_CERTIDAO } from '../services/certidaoNadaConstaService';
import {
  FaArrowLeft, FaFileSignature, FaDownload, FaSpinner, FaCheckCircle, FaTimesCircle,
  FaExclamationTriangle, FaExternalLinkAlt, FaInfoCircle, FaRedo
} from 'react-icons/fa';

// Verificações que a API roda antes de emitir — mesma ordem e chaves do 422
// CERTIDAO_NAO_EMITIVEL (data.inscricao / data.financeiro / data.etica).
const VERIFICACOES = [
  { chave: 'inscricao', titulo: 'Inscrição ativa na OAB/MS' },
  { chave: 'financeiro', titulo: 'Situação financeira' },
  { chave: 'etica', titulo: 'Situação ética' }
];

const PageContainer = styled.div`
  height: 100vh;
  height: 100dvh;
  overflow-y: auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 300px;
    background: linear-gradient(135deg, ${colors.blueDark} 0%, ${colors.blueMedium} 100%);
    opacity: 0.05;
    pointer-events: none;
    z-index: 0;
  }
`;

const ContentContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 120px 2rem 2rem 2rem;
  animation: ${fadeIn} 0.5s ease-out;

  @media (max-width: ${breakpoints.mobile}) {
    padding: 100px 1.5rem 1.5rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 90px 1rem 1rem 1rem;
  }
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  color: ${colors.darkBlue};
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 1.5rem;
  transition: all 0.2s ease;
  font-family: inherit;

  svg {
    font-size: 1rem;
    transition: transform 0.2s ease;
  }

  &:hover {
    color: ${colors.lightBlue};

    svg {
      transform: translateX(-3px);
    }
  }
`;

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a2c3e;
  margin-bottom: 8px;

  @media (max-width: ${breakpoints.mobile}) {
    font-size: 1.4rem;
  }
`;

const PageSubtitle = styled.p`
  color: #5a6e7c;
  margin-bottom: 32px;
  font-size: 0.9rem;
`;

const Card = styled.div`
  background: ${colors.white};
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.05);

  @media (max-width: ${breakpoints.mobile}) {
    padding: 1.25rem;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const CardIcon = styled.div`
  flex-shrink: 0;
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, ${colors.blueLight}15, ${colors.blueDark}15);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 26px;
    color: ${colors.darkBlue};
  }
`;

const CardInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${colors.darkBlue};
  margin: 0 0 0.4rem 0;
`;

const CardDescription = styled.p`
  font-size: 0.875rem;
  color: ${colors.textMuted};
  margin: 0;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.25rem;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${colors.darkBlue};
  color: ${colors.white};
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${colors.blueLight};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }

  .spin {
    animation: girar 1s linear infinite;
  }

  @keyframes girar {
    to { transform: rotate(360deg); }
  }
`;

const SecondaryLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: transparent;
  color: ${colors.darkBlue};
  border: 1px solid ${colors.borderLight};
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${colors.lightBlue};
    color: ${colors.blueLight};
  }
`;

const ResultBox = styled.div`
  margin-top: 1.5rem;
  padding: 1.25rem;
  border-radius: 12px;
  animation: ${fadeIn} 0.3s ease-out;
  background: ${props => ({ sucesso: '#f0fdf4', bloqueio: '#fef2f2', erro: '#fffbeb' }[props.$tipo])};
  border: 1px solid ${props => ({ sucesso: '#bbf7d0', bloqueio: '#fecaca', erro: '#fde68a' }[props.$tipo])};
`;

const ResultTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
  color: ${props => ({ sucesso: '#166534', bloqueio: '#991b1b', erro: '#92400e' }[props.$tipo])};
`;

const ResultText = styled.p`
  font-size: 0.875rem;
  color: #374151;
  margin: 0;
  line-height: 1.5;
`;

const DadosGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(180px, 100%), 1fr));
  gap: 0.75rem 1.5rem;
  margin: 0;

  dt {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${colors.textMuted};
    margin-bottom: 2px;
  }

  dd {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 600;
    color: #1a2c3e;
    overflow-wrap: anywhere;
  }
`;

const Aviso = styled.p`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 0.8rem;
  color: #374151;
  margin: 1rem 0 0 0;
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    color: ${colors.blueLight};
  }
`;

const ErroDownload = styled.p`
  font-size: 0.8rem;
  color: ${colors.red};
  margin: 0.75rem 0 0 0;
`;

const VerificacoesList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 0.75rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const VerificacaoItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  color: #1a2c3e;

  svg {
    flex-shrink: 0;
    font-size: 1.05rem;
    color: ${props => (props.$regular ? '#16a34a' : '#dc2626')};
  }

  span.situacao {
    margin-left: auto;
    font-size: 0.75rem;
    font-weight: 600;
    color: ${props => (props.$regular ? '#166534' : '#991b1b')};
  }
`;

const CertidaoNadaConstaCard = () => {
  // 'idle' | 'emitindo' | 'emitida' | 'bloqueada' | 'erro'
  const [estado, setEstado] = useState('idle');
  const [certidao, setCertidao] = useState(null);
  const [verificacoes, setVerificacoes] = useState(null);
  const [erro, setErro] = useState(null);
  const [baixando, setBaixando] = useState(false);
  const [erroDownload, setErroDownload] = useState(null);

  const baixarPdf = async () => {
    setBaixando(true);
    setErroDownload(null);

    const response = await certidaoNadaConstaService.baixarPdf();
    if (!response.success) {
      setErroDownload(response.error);
    }

    setBaixando(false);
  };

  const emitir = async () => {
    setEstado('emitindo');
    setErro(null);
    setErroDownload(null);

    const response = await certidaoNadaConstaService.emitir();

    if (response.success) {
      setCertidao(response.data);
      setEstado('emitida');
      baixarPdf();
      return;
    }

    if (response.code === CODIGOS_ERRO_CERTIDAO.NAO_EMITIVEL && response.data) {
      setVerificacoes(response.data);
      setEstado('bloqueada');
      return;
    }

    setErro(response.error);
    setEstado('erro');
  };

  const emitindo = estado === 'emitindo';

  return (
    <Card>
      <CardHeader>
        <CardIcon>
          <FaFileSignature />
        </CardIcon>
        <CardInfo>
          <CardTitle>Certidão Nada Consta</CardTitle>
          <CardDescription>
            Atesta que você está com a inscrição ativa e não possui pendências financeiras nem
            processos éticos impeditivos junto à OAB/MS. A emissão é automática e o documento
            pode ser validado por qualquer pessoa pelo QR Code.
          </CardDescription>
        </CardInfo>
      </CardHeader>

      {estado !== 'emitida' && (
        <Actions>
          <PrimaryButton onClick={emitir} disabled={emitindo}>
            {emitindo ? <><FaSpinner className="spin" /> Verificando e emitindo...</> : <><FaFileSignature /> Emitir certidão</>}
          </PrimaryButton>
        </Actions>
      )}

      {estado === 'emitida' && certidao && (
        <ResultBox $tipo="sucesso">
          <ResultTitle $tipo="sucesso">
            <FaCheckCircle /> {certidao.reaproveitada ? 'Você já possui uma certidão vigente' : 'Certidão emitida com sucesso'}
          </ResultTitle>

          <DadosGrid>
            <div>
              <dt>Código de validação</dt>
              <dd>{certidao.numero}</dd>
            </div>
            <div>
              <dt>Emitida em</dt>
              <dd>{formatarDataCertidao(certidao.emitidaEm)}</dd>
            </div>
            <div>
              <dt>Válida até</dt>
              <dd>{formatarDataCertidao(certidao.validaAte)}</dd>
            </div>
          </DadosGrid>

          {certidao.reaproveitada && (
            <Aviso>
              <FaInfoCircle />
              Enquanto a certidão atual estiver dentro da validade, uma nova não é emitida — o PDF
              abaixo é o da certidão vigente.
            </Aviso>
          )}

          <Actions>
            <PrimaryButton onClick={baixarPdf} disabled={baixando}>
              {baixando ? <><FaSpinner className="spin" /> Baixando...</> : <><FaDownload /> Baixar PDF</>}
            </PrimaryButton>
            {certidao.urlValidacao && (
              <SecondaryLink href={certidao.urlValidacao} target="_blank" rel="noopener noreferrer">
                <FaExternalLinkAlt /> Verificar autenticidade
              </SecondaryLink>
            )}
          </Actions>

          {erroDownload && <ErroDownload>{erroDownload}</ErroDownload>}
        </ResultBox>
      )}

      {estado === 'bloqueada' && verificacoes && (
        <ResultBox $tipo="bloqueio">
          <ResultTitle $tipo="bloqueio">
            <FaTimesCircle /> Não foi possível emitir a certidão
          </ResultTitle>

          <VerificacoesList>
            {VERIFICACOES.map(({ chave, titulo }) => {
              const regular = Boolean(verificacoes[chave]?.regular);
              return (
                <VerificacaoItem key={chave} $regular={regular}>
                  {regular ? <FaCheckCircle /> : <FaTimesCircle />}
                  {titulo}
                  <span className="situacao">{regular ? 'Regular' : 'Irregular'}</span>
                </VerificacaoItem>
              );
            })}
          </VerificacoesList>

          <ResultText>
            Regularize os itens marcados acima junto à OAB/MS e tente emitir novamente.
          </ResultText>
        </ResultBox>
      )}

      {estado === 'erro' && (
        <ResultBox $tipo="erro">
          <ResultTitle $tipo="erro">
            <FaExclamationTriangle /> Erro ao emitir a certidão
          </ResultTitle>
          <ResultText>{erro}</ResultText>
          <Actions>
            <PrimaryButton onClick={emitir}>
              <FaRedo /> Tentar novamente
            </PrimaryButton>
          </Actions>
        </ResultBox>
      )}
    </Card>
  );
};

const CertidoesOnlinePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleGoBack = () => navigate('/home');

  // Mesma regra do card da Home: acesso direto pela URL também é barrado
  // pra PJ, PF não inscrita e Estagiário(a) — com o motivo na tela, em vez de
  // voltar pra Home sem explicação.
  if (!podeAcessarCertidoesOnline(getContextoAcesso(user, authService.getOABData()))) {
    return (
      <MainLayout>
        <PageContainer>
          <ContentContainer>
            <BackButton onClick={handleGoBack}>
              <FaArrowLeft /> Voltar para Home
            </BackButton>

            <PageTitle>Certidões Online</PageTitle>

            <ResultBox $tipo="bloqueio">
              <ResultTitle $tipo="bloqueio">
                <FaTimesCircle /> Certidão indisponível para este login
              </ResultTitle>
              <ResultText>
                A Certidão Nada Consta é exclusiva de advogado(a) inscrito(a) na OAB/MS. Para emiti-la,
                entre com o login do(a) advogado(a), usando o CPF e a senha.
              </ResultText>
            </ResultBox>
          </ContentContainer>
        </PageContainer>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <PageContainer>
        <ContentContainer>
          <BackButton onClick={handleGoBack}>
            <FaArrowLeft /> Voltar para Home
          </BackButton>

          <PageTitle>Certidões Online</PageTitle>
          <PageSubtitle>Emita certidões automáticas a qualquer hora, sem precisar abrir um pedido</PageSubtitle>

          <CertidaoNadaConstaCard />
        </ContentContainer>
      </PageContainer>
    </MainLayout>
  );
};

export default CertidoesOnlinePage;
